import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {
  Button,
  Checkbox,
  CircleIcon,
  ContentPanel,
  ShapeIcon,
  SquareIcon,
} from '@taopix/taopix-design-system';
import {AssetType, BoxType, BoxTypeShape, NumBool} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import ToolPaletteSection from '../../../ToolPalette/ToolPaletteSection';
import PaletteInformationMessage from '../PaletteInformationMessage';

export type ShapeProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

type ShapeExperienceValues = {
  shapeSelection: boolean;
  shapeLockBoxProportions: boolean;
};

const Shape = ({sectionKey, item}: ShapeProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation();

  const {experience} = useExperience();
  const {pictureBoxOptions, shapeBoxOptions, stickerOptions} = experience.EDITOR;

  const [showSection, setShowSection] = useState<boolean>(false);
  const [disableSection, setDisableSection] = useState<boolean>(false);
  const [notAvailableMessage, setNotAvailableMessage] = useState<string>();

  const [lockProportions, setLockProportions] = useState<number>(
    item?.boxconstrainproportions as number | 0
  );

  const [shapeOptions, setShapeOptions] = useState<ShapeExperienceValues>({
    shapeSelection: true,
    shapeLockBoxProportions: true,
  });

  useEffect(() => {
    setLockProportions(item?.boxconstrainproportions as number);
  }, [item?.boxconstrainproportions]);

  const isActiveInExperience = (item: ComputedPageItem) => {
    const boxType = item.type;
    // Return whether or not this option is enabled in the experience for the selected item
    if (
      boxType === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage
    ) {
      setShapeOptions({
        shapeSelection: pictureBoxOptions.shapeSelection,
        shapeLockBoxProportions: pictureBoxOptions.shapeLockBoxProportions,
      });
      return pictureBoxOptions.shape;
    }
    if (boxType === BoxType.Shape && [BoxTypeShape.Oval, BoxTypeShape.Rect].includes(item?.shape)) {
      setShapeOptions({
        shapeSelection: shapeBoxOptions.shapeSelection,
        shapeLockBoxProportions: shapeBoxOptions.shapeLockBoxProportions,
      });
      return shapeBoxOptions.shape;
    }
    if (AssetType.ScrapbookImage === item.hiresfiletype) {
      setShapeOptions({
        shapeSelection: stickerOptions.shapeSelection,
        shapeLockBoxProportions: stickerOptions.shapeLockBoxProportions,
      });
      return stickerOptions.shape;
    } else {
      return false;
    }
  };

  useEffect(() => {
    // This is an advanced option, so check whether advanced editing is on
    const canShowAdvancedOptions = ui.state.canEditPageLayout;

    // If there is a selected item
    if (item && item.ref !== -1) {
      // Show or hide this section based on the relevant experience and advanced editing configuration
      setShowSection(isActiveInExperience(item) && canShowAdvancedOptions);
      // Enable or disable this section based on the item's protection
      setDisableSection(
        NumBool.True === item?.protectcontents ||
          (item.errors?.imageMissing as boolean) ||
          ui.state.swapActive
      );
      // Set the message to 'not available' in case the section was hidden or disabled
      setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
    } else {
      // If no item is selected, hide the section button
      setShowSection(false);
      // And show the 'please select an item' message
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [
    item?.asset,
    item?.asset?.assetType,
    item?.type,
    item?.image,
    item?.shape,
    item?.ref,
    ui.state.swapActive,
  ]);

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'space-y-sm',
    'lg:flex-1',
    'sm:pb-md',
    'sm:h-mobilePaletteContentHeight',
    'sm:max-w-full',
    'overflow-y-auto'
  );

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}`
  ) {
    return null;
  }

  return (
    <ToolPaletteSection
      id={'shapePalette'}
      title={t('boxpalettes:str_TitleShape')}
      buttonIcon={<ShapeIcon />}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      useFocusMode
      subSection={true}
      showButton={showSection}
      disabled={disableSection}
      buttonLayout="horizontal"
      parentselected={true}
    >
      <div className={wrapperClasses} tabIndex={-1}>
        {!showSection || disableSection ? (
          // If the palette button has been hidden or disabled (by the experience, box protection or nothing is selected),
          // show the relevant 'unavailable' message in the palette in case the palette was left open when its status changed
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          // Only show the sub options if the palette is allowed in the experience and the content is not protected
          <>
            {shapeOptions.shapeSelection && (
              <ContentPanel heading={t('boxpalettes:str_TitleShape')}>
                <div className={'space-y-sm'}>
                  <Button
                    data-testid={'box-settings-shape-square-button'}
                    className={'w-full'}
                    label={t('boxpalettes:str_LabelSquareOrRectangle')}
                    buttonStyle={'tertiary'}
                    startIcon={<SquareIcon />}
                    labelAlignment={'left'}
                    size={'large'}
                    onClick={() => {
                      app.core
                        .dispatch('SHAPE.CHANGE_SHAPE', {shape: BoxTypeShape.Rect})
                        .catch(error => {
                          console.error(error);
                        });
                    }}
                    aria-pressed={item?.shape === BoxTypeShape.Rect}
                    disabled={NumBool.True === item?.protectcontents}
                  />
                  <Button
                    data-testid={'box-settings-shape-circle-button'}
                    className={'w-full'}
                    label={t('boxpalettes:str_LabelCircleOrOval')}
                    buttonStyle={'tertiary'}
                    startIcon={<CircleIcon />}
                    labelAlignment={'left'}
                    size={'large'}
                    onClick={() => {
                      app.core.dispatch('SHAPE.CHANGE_SHAPE', {shape: BoxTypeShape.Oval});
                    }}
                    aria-pressed={item?.shape === BoxTypeShape.Oval}
                    disabled={NumBool.True === item?.protectcontents}
                  />
                </div>
              </ContentPanel>
            )}
            {shapeOptions.shapeLockBoxProportions && (
              <ContentPanel heading={t('boxpalettes:str_TitleBoxProportions')}>
                <Checkbox
                  data-testid={'box-settings-shape-boxproportions-checkbox'}
                  label={t('boxpalettes:str_LabelLockBoxProportions')}
                  id={'lock-box-proportions-shape'}
                  key={`shape-lock-proportions-${lockProportions}-${
                    item?.boxconstrainproportions === 1
                  }`}
                  appearance={'checkbox'}
                  defaultChecked={lockProportions === 1}
                  onChange={(isChecked: boolean) => {
                    if (isChecked) {
                      app.core.dispatch('SHAPE.CONSTRAIN_PROPORTIONS', 1).catch(error => {
                        console.error(error);
                      });
                    } else {
                      app.core.dispatch('SHAPE.CONSTRAIN_PROPORTIONS', 0).catch(error => {
                        console.error(error);
                      });
                    }
                  }}
                  disabled={
                    NumBool.True === item?.protectconstrainproportions ||
                    NumBool.True === item?.protectcontents ||
                    NumBool.True === item?.protecthorizontalsize ||
                    NumBool.True === item?.protectverticalsize
                  }
                />
              </ContentPanel>
            )}
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default Shape;
